import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { authService } from '../services/auth.service'
import NavBar from '../components/NavBar'
import { LoginResponseDto } from '@ivy-monorepo/shared'
import { LocalStorageKey } from '../helpers/constants'
import AccountProfile from '../components/AccountProfile'
import Icon from '../components/Icon'
import GlobalSearch from '../components/GlobalSearch'
import { SearchProvider } from '../contexts/SearchContext'
import { PermissionsProvider } from '../contexts/PermissionsContext'

const IDLE_TIMEOUT = 4 * 60 * 60 * 1000 // 4 hours without activity, same as the admin session
const WARNING_BEFORE_IDLE_LOGOUT = 30 * 1000 // 30 seconds in milliseconds

export const AuthenticatedLayout = () => {
  const [authData, setAuthData] = useState<LoginResponseDto | null>(null)
  const [isNavCollapsed, setIsNavCollapsed] = useState(false)
  const [theme, setTheme] = useState('light')
  const [showIdleWarning, setShowIdleWarning] = useState(false)
  const [isIdleWarningFadingOut, setIsIdleWarningFadingOut] = useState(false)
  const [lastActivityTime, setLastActivityTime] = useState(Date.now())
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  // Actual remaining idle time derived from last activity
  const [idleWarningRemainingSeconds, setIdleWarningRemainingSeconds] = useState(
    WARNING_BEFORE_IDLE_LOGOUT / 1000
  )
  const navigate = useNavigate()
  const authTimeoutBarRef = useRef<HTMLDivElement>(null)

  const handleLogout = useCallback(async () => {
    try {
      await authService.logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }, [navigate])

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
      if (window.innerWidth <= 768) {
        setIsNavCollapsed(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const idleTimeoutInterval = setInterval(() => {
      const currentTime = Date.now()
      const timeSinceLastActivity = currentTime - lastActivityTime

      if (timeSinceLastActivity > IDLE_TIMEOUT) {
        handleLogout()
      }

      if (timeSinceLastActivity > IDLE_TIMEOUT - WARNING_BEFORE_IDLE_LOGOUT) {
        setIdleWarningRemainingSeconds(Math.floor((IDLE_TIMEOUT - timeSinceLastActivity) / 1000))
        setShowIdleWarning(true)
      }
    }, 1000)

    return () => clearInterval(idleTimeoutInterval)
  }, [lastActivityTime, handleLogout])

  const handleActivity = useCallback(() => {
    setLastActivityTime(Date.now())

    if (showIdleWarning) {
      setIsIdleWarningFadingOut(true)
      setTimeout(() => {
        setIdleWarningRemainingSeconds(WARNING_BEFORE_IDLE_LOGOUT / 1000)
        setShowIdleWarning(false)
        setIsIdleWarningFadingOut(false)
      }, 300)
    }
  }, [showIdleWarning])

  useEffect(() => {
    const currentTheme = localStorage.getItem(LocalStorageKey.theme) ?? 'light'
    setTheme(currentTheme)
    document.documentElement.setAttribute('data-theme', currentTheme)
  }, [])

  // Remove visual-only countdown; we now display actual remaining time

  useEffect(() => {
    const checkAuth = async () => {
      const storedAuth = localStorage.getItem(LocalStorageKey.auth)
      if (storedAuth) {
        const auth = JSON.parse(storedAuth) as LoginResponseDto
        setAuthData(auth)
      } else {
        navigate('/login')
      }
    }
    checkAuth()
  }, [navigate])

  useEffect(() => {
    const handleAuthChange = (isAuthenticated: boolean) => {
      if (!isAuthenticated) {
        navigate('/login')
      }
    }

    const unsubscribe = authService.subscribe(handleAuthChange)

    return () => {
      unsubscribe()
    }
  }, [navigate])

  useEffect(() => {
    // Add event listeners
    // window.addEventListener('mousemove', handleActivity)
    window.addEventListener('keydown', handleActivity)
    window.addEventListener('click', handleActivity)
    window.addEventListener('scroll', handleActivity)

    // Cleanup
    return () => {
      //window.removeEventListener('mousemove', handleActivity)
      window.removeEventListener('keydown', handleActivity)
      window.removeEventListener('click', handleActivity)
      window.removeEventListener('scroll', handleActivity)
    }
  }, [isIdleWarningFadingOut, handleActivity, handleLogout])

  const updateAuthTimeoutBar = () => {
    const remainingPercentage = authService.getAuthRemainingPercentage()
    if (authTimeoutBarRef.current) {
      authTimeoutBarRef.current.style.width = `${remainingPercentage}%`
    }
  }

  useEffect(() => {
    const interval = setInterval(updateAuthTimeoutBar, 1000)
    return () => clearInterval(interval)
  }, [])

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    localStorage.setItem(LocalStorageKey.theme, newTheme)
    document.documentElement.setAttribute('data-theme', newTheme)
  }

  return (
    <PermissionsProvider>
    <SearchProvider>
      <div className={`authenticated-layout ${isNavCollapsed ? 'nav-collapsed' : ''}`}>
        <div className="auth-timeout-bar">
          <div ref={authTimeoutBarRef}></div>
        </div>
        {(showIdleWarning || isIdleWarningFadingOut) && (
          <div className={`idle-warning-toast ${isIdleWarningFadingOut ? 'fading-out' : ''}`}>
            <div className="toast-content">
              <Icon name="Warning" />
              <span>
                You've been idle for a while. You will be logged out in{' '}
                {idleWarningRemainingSeconds < 0 ? 0 : idleWarningRemainingSeconds}{' '}
                {idleWarningRemainingSeconds === 1 ? 'second' : 'seconds'}.
              </span>
            </div>
          </div>
        )}
        <div className="layout-container">
          <div className="nav-bar">
            <NavBar
              role={authData?.account?.role.toString() ?? ''}
              onCollapse={setIsNavCollapsed}
              isCollapsed={isNavCollapsed}
              isMobile={isMobile}
            />
          </div>
          <div className="content-area">
            <div className="content-container">
              <div className="top-bar">
                <div className="top-bar-content">
                  <Icon name="MagnifyingGlass" />
                  <GlobalSearch />
                  {authData && (
                    <AccountProfile
                      authData={authData}
                      handleLogout={handleLogout}
                      theme={theme}
                      onThemeChange={toggleTheme}
                      isMobile={isMobile}
                    />
                  )}
                </div>
              </div>
              <div className="content-wrapper">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SearchProvider>
    </PermissionsProvider>
  )
}

export default AuthenticatedLayout
